import { getDeviceAndUserStore } from '../storage/stores';
import { EventsReportResult } from '../types';
import { getApi } from '.';
import {
  WebEvent,
  EventsReportResponse,
  ScreenTimeEvent,
} from '../types/api/events';
import { now, toSeconds } from '../lib/dateTime';
import {
  buildScreenTime,
  buildNavigation,
  removeScreenTimeReport,
  buildScreenTimeWithDelegation,
} from '../report';
import { Mutex } from 'async-mutex';

// Since we handle a queue of events on the reports, the execution needs to be synchronized.
const reportMutex = new Mutex();

/**
 * Report a web navigation event
 *
 * @remarks
 * It will also include the enqueued screen time events (if any, up to PENDING_QUEUE_EVENTS_ONE_POST_LIMIT events)
 */
export const report = (params: WebEvent) =>
  reportMutex.runExclusive(async () => {
    const timestamp = toSeconds(await now()); // Seconds precision
    const screenTimeEvents = buildScreenTime(timestamp);
    const navigationEvents = buildNavigation(params, timestamp);
    const body =
      screenTimeEvents.length > 0
        ? {
            events: navigationEvents,
            screen_time: { report: screenTimeEvents },
          }
        : { events: navigationEvents };

    return sendEvents(body, screenTimeEvents);
  });

/**
 * Report pending screen time events (if any, up to PENDING_QUEUE_EVENTS_ONE_POST_LIMIT events)
 */
export const reportScreenTime = () =>
  reportMutex.runExclusive(async () => {
    const screenTimeEvents = buildScreenTimeWithDelegation();
    // Report only if there are any pending events
    if (screenTimeEvents.length > 0) {
      console.info(
        `Reporting ${screenTimeEvents.length} screen time event(s)...`
      );
      const body = {
        screen_time: {
          report: screenTimeEvents,
        },
      };
      return sendEvents(body, screenTimeEvents);
    }
  });

const sendEvents = <Body>(
  requestBody: Body,
  screenTime: ScreenTimeEvent[]
): Promise<EventsReportResult> => {
  const data = getDeviceAndUserStore().get();
  if (!data) {
    return Promise.reject(Error('Store data not properly initialized!'));
  }

  return getApi()
    .deviceUserEvents.post(requestBody, null, {
      deviceUId: data.device,
      userUId: data.user,
    })
    .then((response: EventsReportResponse) => {
      // Remove already send events from the pending queue to report
      removeScreenTimeReport(screenTime);

      return {
        devicePolicyTimestamp: response.headers.get('device-policy-timestamp'),
        countableSeconds: response.data.screen_time.countable_seconds,
      };
    });
};
